import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiBusinessBaseStatistics } from '../../../../api/business_base/ApiBusinessBaseStatistics';
import { AVAILABLE_BUSINESS_BASE_REQUEST_ORIGINS } from '../../../../api/business_base/models/BusinessBaseStatisticsModels';
import {
  DateRangeFilter,
  OriginFilter,
  ResultsCountFilter,
  ValueFilter,
} from '../../../hexa_api/statistics/StatisticsFilters';
import StatisticsRankedList from '../../../hexa_api/statistics/StatisticsRankedList';
import StatisticsSection from '../../../hexa_api/statistics/StatisticsSection';
import { useMostSearchedStats } from '../../../hexa_api/statistics/useMostSearchedStats';

interface MostRequestedTabProps {
  onFetchError: (message: string) => void;
}

const MostRequestedTab = ({ onFetchError }: MostRequestedTabProps) => {
  const { t } = useTranslation();

  const fetchMostRequested = useCallback(
    (params: Parameters<typeof ApiBusinessBaseStatistics.mostRequested>[0]) =>
      ApiBusinessBaseStatistics.mostRequested(params).then(
        (response) => response.most_requested
      ),
    []
  );

  const mostRequestedStats = useMostSearchedStats(
    fetchMostRequested,
    onFetchError
  );

  return (
    <StatisticsSection
      title={t('business_base_statistics.most_requested_title')}
      subtitle={t('business_base_statistics.most_requested_subtitle')}
      icon={<VisibilityOutlinedIcon />}
      filtersTitle={t('entity_statistics.filters')}
      onResetFilters={mostRequestedStats.resetFilters}
      isLoading={mostRequestedStats.isLoading}
      loadingText={t('business_base_statistics.most_requested_loading')}
      isEmpty={mostRequestedStats.results.length === 0}
      emptyText={t('business_base_statistics.most_requested_empty')}
      filters={
        <>
          <DateRangeFilter state={mostRequestedStats} />
          <ResultsCountFilter state={mostRequestedStats} />
          <ValueFilter
            state={mostRequestedStats}
            label={t('business_base_statistics.value_filter_label')}
            placeholder={t('business_base_statistics.value_filter_placeholder')}
          />
          <OriginFilter
            state={mostRequestedStats}
            options={AVAILABLE_BUSINESS_BASE_REQUEST_ORIGINS}
            labelKeyPrefix="business_base_statistics.origin_"
          />
        </>
      }
    >
      <StatisticsRankedList
        valueLabel={t('business_base_statistics.requested_count')}
        entries={mostRequestedStats.results.map((entry, index) => ({
          id: `${entry.origin}-${entry.type}-${entry.value}-${index}`,
          label: entry.value,
          // The same value can be ranked once per origin, so the origin is shown to tell them apart
          tags: [
            entry.type,
            t(`business_base_statistics.origin_${entry.origin}`).toUpperCase(),
          ],
          value: entry.requested_count,
        }))}
      />
    </StatisticsSection>
  );
};

export default MostRequestedTab;
